import type { ComponentPublicInstance } from 'vue';
import { ref } from 'vue';

export function useStoriesProgressBar() {
  const progressBars = ref<(HTMLElement | null)[]>([]);

  const setProgressBarRef =
    (index: number) => (el: Element | ComponentPublicInstance | null) => {
      if (el && el instanceof HTMLElement) {
        progressBars.value[index] = el;
      }
    };

  const resetAllProgressBars = () => {
    progressBars.value.forEach((bar) => {
      if (bar) {
        bar.style.width = '0%';
        bar.style.transition = 'none';
      }
    });
  };

  const getProgressBarWidth = (bar: HTMLElement): number => {
    const computedStyle = window.getComputedStyle(bar);
    const currentWidthPx = parseFloat(computedStyle.width) || 0;
    const containerWidthPx = bar.parentElement
      ? parseFloat(window.getComputedStyle(bar.parentElement).width) || 0
      : 0;

    if (containerWidthPx > 0 && currentWidthPx > 0) {
      return (currentWidthPx / containerWidthPx) * 100;
    }

    const styleWidth = bar.style.width || '';
    if (!styleWidth) {
      return 0;
    }

    if (styleWidth.includes('%')) {
      return parseFloat(styleWidth) || 0;
    }

    if (containerWidthPx > 0) {
      return (parseFloat(styleWidth) / containerWidthPx) * 100;
    }

    return 0;
  };

  const setProgressBarWidth = (bar: HTMLElement, width: number) => {
    bar.style.width = `${width}%`;
  };

  const resetProgressBar = (bar: HTMLElement) => {
    bar.style.width = '0%';
    bar.style.transition = 'none';
  };

  const startProgressBarAnimation = (
    bar: HTMLElement,
    duration: number,
    resetDelay: number,
  ) => {
    resetProgressBar(bar);

    // Force reflow to ensure the reset is applied
    void bar.offsetWidth;

    setTimeout(() => {
      bar.style.transition = `width ${duration}ms linear`;
      bar.style.width = '100%';
    }, resetDelay);
  };

  return {
    progressBars,
    setProgressBarRef,
    resetAllProgressBars,
    getProgressBarWidth,
    setProgressBarWidth,
    resetProgressBar,
    startProgressBarAnimation,
  };
}
